/* ============================================================
   <image-lightbox> — fullscreen product-image viewer with zoom.

   Patterns borrowed from PhotoSwipe v5:
   - ZoomLevel model: fit / secondary / max  (src/js/slide/zoom-level.js)
   - PanBounds clamping per axis  (src/js/slide/pan-bounds.js)
   - Pointer gesture state machine  (src/js/gestures/gestures.js)
   - Zoom-to-cursor translate-compensation  (Gestures._panAroundPoint)
   - Singleton overlay, body scroll lock w/ scrollbar comp
   - Bail on Cmd/Ctrl/middle-click (browser "open in new tab" survives)
   - _openSeq counter to cancel stale opens on rapid clicks

   Reuses global trapFocus() / removeTrapFocus() defined in
   assets/main.js (loaded earlier via layout/theme.liquid).
   ============================================================ */

(() => {
  if (window.__imageLightboxLoaded) return;
  window.__imageLightboxLoaded = true;

  // ---- Constants lifted from PhotoSwipe v5 ----------------------------
  const WHEEL_STEP = 0.0015;        // newScale = scale * (1 - dy * STEP)
  const DOUBLE_TAP_DELAY = 300;     // ms
  const MIN_TAP_DISTANCE = 25;      // px
  const DRAG_HYSTERESIS = 10;       // px before drag commits
  const MAX_IMAGE_WIDTH = 4000;     // secondary-zoom upper bound

  // ---- Math helpers ---------------------------------------------------
  const clamp = (v, lo, hi) => (lo > hi ? Math.min(lo, Math.max(hi, v)) : Math.max(lo, Math.min(hi, v)));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const noopTrap = () => {};
  const _trapFocus = typeof trapFocus === 'function' ? trapFocus : noopTrap;
  const _removeTrapFocus = typeof removeTrapFocus === 'function' ? removeTrapFocus : noopTrap;

  // rAF-throttle: collapse multiple rapid calls into one frame.
  // Prevents resize / variant-change handlers from thrashing layout.
  function rafThrottle(fn) {
    let pending = false;
    let lastArgs;
    return function throttled(...args) {
      lastArgs = args;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; fn.apply(this, lastArgs); });
    };
  }

  // ---- Slide data extraction from existing gallery DOM ----------------
  // No edits to product-media.liquid required: data-original-width/height
  // already live on the inner .zoom-image element.
  function buildSlidesFromGallery(gallery) {
    const items = gallery.querySelectorAll('.media-viewer .media-viewer__item');
    const slides = [];
    items.forEach((item) => {
      if (item.dataset.mediaType !== 'image') return;
      // Variant-grouped items get display:none — skip
      if (item.style.display === 'none') return;
      const link = item.querySelector('a.js-zoom-link');
      const productImg = item.querySelector('img.product-image');
      if (!link || !productImg) return;
      const zoomImg = item.querySelector('.zoom-image');
      const w = Number((zoomImg && zoomImg.dataset.originalWidth) || productImg.naturalWidth || 0);
      const h = Number((zoomImg && zoomImg.dataset.originalHeight) || productImg.naturalHeight || 0);
      slides.push({
        item,
        src: link.getAttribute('href') || productImg.currentSrc || productImg.src,
        preview: productImg.currentSrc || productImg.src,
        width: w || 1600,
        height: h || 1600,
        alt: productImg.alt || '',
      });
    });
    return slides;
  }

  // ---- The custom element --------------------------------------------
  if (!customElements.get('image-lightbox')) {
    class ImageLightbox extends HTMLElement {
      constructor() {
        super();
        this._slides = [];
        this._index = 0;
        this._scale = 1;
        this._tx = 0;
        this._ty = 0;
        this._zoom = { fit: 1, secondary: 1, max: 1, min: 1, ceil: 1 };
        this._pointers = new Map();         // pointerId -> {x, y, startX, startY}
        this._pinch = null;                 // { d0, scale0, cx, cy }
        this._panning = false;
        this._lastTap = 0;
        this._lastTapPoint = null;
        this._openSeq = 0;
        this._rafPending = false;
        this._opener = null;
        this._gallery = null;
        // Bind once in constructor so add/remove listener pairs reference the
        // same function — and so we don't allocate new bound fns on every open.
        this._onVariantChange = this._onVariantChange.bind(this);
        this._onResize = rafThrottle(this._onResize.bind(this));
        this._onKey = this._onKey.bind(this);
        this._onWheel = this._onWheel.bind(this);
        this._onPointerDown = this._onPointerDown.bind(this);
        this._onPointerMove = this._onPointerMove.bind(this);
        this._onPointerUp = this._onPointerUp.bind(this);
        // Cache the stage's bounding rect per-gesture so pointermove handlers
        // don't trigger getBoundingClientRect()/layout reads at 60+fps.
        this._stageRect = null;
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', 'Image viewer');
        this.innerHTML = `
          <div class="lightbox__overlay"></div>
          <div class="lightbox__stage" data-zoomed="false" data-panning="false">
            <img class="lightbox__img" alt="" draggable="false">
            <div class="lightbox__spinner" aria-hidden="true"></div>
          </div>
          <div class="lightbox__top">
            <div class="lightbox__counter" aria-live="polite"></div>
            <button type="button" class="lightbox__close" aria-label="Close image viewer">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 3 L13 13 M13 3 L3 13"/></svg>
            </button>
          </div>
          <button type="button" class="lightbox__prev" aria-label="Previous image">
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M10.5 2 4.5 8l6 6 1.5-1.5L7.5 8l4.5-4.5z"/></svg>
          </button>
          <button type="button" class="lightbox__next" aria-label="Next image">
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M5.5 2 11.5 8l-6 6L4 12.5 8.5 8 4 3.5z"/></svg>
          </button>
          <div class="lightbox__bottom">
            <button type="button" class="lightbox__zoom-out" aria-label="Zoom out">−</button>
            <button type="button" class="lightbox__reset" aria-label="Reset zoom">⤢</button>
            <button type="button" class="lightbox__zoom-in" aria-label="Zoom in">+</button>
          </div>
        `;
        this.stage = this.querySelector('.lightbox__stage');
        this.img = this.querySelector('.lightbox__img');
        this.counter = this.querySelector('.lightbox__counter');
        this.closeBtn = this.querySelector('.lightbox__close');
        this.prevBtn = this.querySelector('.lightbox__prev');
        this.nextBtn = this.querySelector('.lightbox__next');
        this.zoomInBtn = this.querySelector('.lightbox__zoom-in');
        this.zoomOutBtn = this.querySelector('.lightbox__zoom-out');
        this.resetBtn = this.querySelector('.lightbox__reset');

        // Buttons stopPropagation so they don't bubble into the stage-close handler below.
        const stop = (fn) => (evt) => { evt.stopPropagation(); fn(); };
        this.closeBtn.addEventListener('click', stop(() => this.close()));
        this.prevBtn.addEventListener('click', stop(() => this.prev()));
        this.nextBtn.addEventListener('click', stop(() => this.next()));
        this.zoomInBtn.addEventListener('click', stop(() => this._zoomBy(1.4, this._stageCenter())));
        this.zoomOutBtn.addEventListener('click', stop(() => this._zoomBy(1 / 1.4, this._stageCenter())));
        this.resetBtn.addEventListener('click', stop(() => this._setZoom(this._zoom.fit, this._stageCenter(), true)));
        // Click backdrop (empty area around image) → close. The image has pointer-events:none,
        // so clicks on the image dark area come through as target === stage.
        this.stage.addEventListener('click', (evt) => {
          if (evt.target !== this.stage) return;
          if (this._justPanned) return;
          this.close();
        });
        // When the image finishes loading, apply fit and clear spinner
        this.img.addEventListener('load', () => {
          this.removeAttribute('data-loading');
          this._computeZoomLevels();
          this._setZoom(this._zoom.fit, this._stageCenter(), false);
        });
      }

      // -- Open / close ------------------------------------------------
      open(gallery, startItem) {
        this._openSeq += 1;
        const seq = this._openSeq;
        this._gallery = gallery;
        this._slides = buildSlidesFromGallery(gallery);
        if (this._slides.length === 0) return;
        // Find starting index = the clicked item (or 0)
        const startIdx = Math.max(0, this._slides.findIndex((s) => s.item === startItem));
        this._index = startIdx;

        this._opener = document.activeElement;
        // Scroll lock w/ scrollbar-width compensation
        const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
        document.body.classList.add('lightbox-open');

        this.setAttribute('data-open', 'true');

        // Listeners — bind for lifetime of open()
        document.addEventListener('keydown', this._onKey);
        window.addEventListener('resize', this._onResize);
        this.stage.addEventListener('wheel', this._onWheel, { passive: false });
        this.stage.addEventListener('pointerdown', this._onPointerDown);
        this.stage.addEventListener('pointermove', this._onPointerMove);
        this.stage.addEventListener('pointerup', this._onPointerUp);
        this.stage.addEventListener('pointercancel', this._onPointerUp);
        if (this._gallery) {
          const section = this._gallery.closest('.js-product');
          if (section) section.addEventListener('on:variant:change', this._onVariantChange);
        }

        this._renderSlide(true);
        // Trap focus after the slide renders, in case the user click-spammed
        // and a more recent open() invalidated this one.
        if (seq === this._openSeq) {
          _trapFocus(this, this.closeBtn);
        }
      }

      close() {
        if (this.getAttribute('data-open') !== 'true') return;
        this.removeAttribute('data-open');
        document.body.classList.remove('lightbox-open');
        document.body.style.paddingRight = '';

        document.removeEventListener('keydown', this._onKey);
        window.removeEventListener('resize', this._onResize);
        this.stage.removeEventListener('wheel', this._onWheel);
        this.stage.removeEventListener('pointerdown', this._onPointerDown);
        this.stage.removeEventListener('pointermove', this._onPointerMove);
        this.stage.removeEventListener('pointerup', this._onPointerUp);
        this.stage.removeEventListener('pointercancel', this._onPointerUp);
        if (this._gallery) {
          const section = this._gallery.closest('.js-product');
          if (section) section.removeEventListener('on:variant:change', this._onVariantChange);
        }
        this._pointers.clear();
        this._pinch = null;
        this._panning = false;
        this.stage.dataset.panning = 'false';

        _removeTrapFocus(this._opener || undefined);
        this._opener = null;
        this._gallery = null;
      }

      // -- Slide change ------------------------------------------------
      prev() { this._goTo(this._index - 1); }
      next() { this._goTo(this._index + 1); }
      _goTo(idx) {
        if (this._slides.length === 0) return;
        const n = this._slides.length;
        const next = ((idx % n) + n) % n;
        if (next === this._index) return;
        this._index = next;
        this._renderSlide(true);
      }

      _renderSlide(showSpinner) {
        const slide = this._slides[this._index];
        if (!slide) return;
        this.counter.textContent = `${this._index + 1} / ${this._slides.length}`;
        // Show prev/next state
        const single = this._slides.length <= 1;
        this.prevBtn.style.display = single ? 'none' : '';
        this.nextBtn.style.display = single ? 'none' : '';
        // Reset transform before swap
        this._scale = 1; this._tx = 0; this._ty = 0;
        this._applyTransform(false);
        if (showSpinner) this.setAttribute('data-loading', 'true');
        this.img.alt = slide.alt;
        // Start with preview (already in browser cache), then upgrade to hi-res
        if (this.img.src !== slide.src) {
          if (slide.preview && this.img.src !== slide.preview) {
            this.img.src = slide.preview;
          }
          // Kick hi-res load
          const hi = new Image();
          hi.onload = () => {
            // Guard against rapid slide changes
            if (this._slides[this._index] !== slide) return;
            this.img.src = slide.src;
          };
          hi.onerror = () => this.removeAttribute('data-loading');
          hi.src = slide.src;
        } else {
          // Same src — apply fit immediately
          this._computeZoomLevels();
          this._setZoom(this._zoom.fit, this._stageCenter(), false);
          this.removeAttribute('data-loading');
        }
        // Preload neighbor
        if (this._slides.length > 1) {
          const nIdx = (this._index + 1) % this._slides.length;
          const pre = new Image();
          pre.src = this._slides[nIdx].src;
        }
      }

      // -- Zoom model (PhotoSwipe v5 zoom-level.js) --------------------
      _computeZoomLevels() {
        const slide = this._slides[this._index];
        if (!slide) return;
        const vW = this.stage.clientWidth;
        const vH = this.stage.clientHeight;
        const imgW = slide.width || this.img.naturalWidth || vW;
        const imgH = slide.height || this.img.naturalHeight || vH;
        const fit = Math.min(1, Math.min(vW / imgW, vH / imgH));
        const secondaryRaw = Math.min(1, fit * 3);
        const secondaryCap = imgW > 0 ? MAX_IMAGE_WIDTH / imgW : secondaryRaw;
        const secondary = Math.min(secondaryRaw, secondaryCap > 0 ? secondaryCap : secondaryRaw);
        const max = Math.max(1, fit * 4);
        this._zoom = {
          fit,
          secondary,
          max,
          min: Math.min(fit, secondary),
          ceil: Math.max(secondary, max),
          imgW, imgH, vW, vH,
        };
        // Sync image element width to its natural width so transform-origin math works
        this.img.style.width = `${imgW}px`;
        this.img.style.height = `${imgH}px`;
      }

      _stageCenter() {
        return { x: this.stage.clientWidth / 2, y: this.stage.clientHeight / 2 };
      }


      // PanBounds (PhotoSwipe pan-bounds.js): clamp tx, ty for a given scale.
      _correctPan(scale, tx, ty) {
        const { imgW, imgH, vW, vH } = this._zoom;
        const elW = imgW * scale;
        const elH = imgH * scale;
        let minX, maxX, minY, maxY;
        if (elW > vW) { maxX = 0; minX = vW - elW; }
        else { maxX = minX = (vW - elW) / 2; }
        if (elH > vH) { maxY = 0; minY = vH - elH; }
        else { maxY = minY = (vH - elH) / 2; }
        return {
          tx: clamp(tx, minX, maxX),
          ty: clamp(ty, minY, maxY),
        };
      }

      // Apply a new scale anchored at a viewport point (cx, cy).
      // PhotoSwipe v5 _panAroundPoint formula. _correctPan handles centering
      // when the scaled image is smaller than the viewport on either axis.
      _setZoom(newScale, anchor, animate) {
        if (!this._zoom || !this._zoom.imgW || !this._scale) return;
        const target = clamp(newScale, this._zoom.min, this._zoom.ceil);
        const ratio = target / this._scale;
        const nx = anchor.x - (anchor.x - this._tx) * ratio;
        const ny = anchor.y - (anchor.y - this._ty) * ratio;
        const corrected = this._correctPan(target, nx, ny);
        this._scale = target;
        this._tx = corrected.tx;
        this._ty = corrected.ty;
        this._applyTransform(animate);
        this.stage.dataset.zoomed = (target > this._zoom.fit + 1e-3) ? 'true' : 'false';
      }

      _zoomBy(factor, anchor) {
        const next = this._scale * factor;
        // Snap up from fit straight to secondary on first click
        if (factor > 1 && this._scale <= this._zoom.fit + 1e-3) {
          this._setZoom(this._zoom.secondary, anchor, true);
        } else {
          this._setZoom(next, anchor, true);
        }
      }

      _applyTransform(animate) {
        if (animate) this.img.classList.add('is-animating');
        else this.img.classList.remove('is-animating');
        this.img.style.transform = `translate3d(${this._tx}px, ${this._ty}px, 0) scale(${this._scale})`;
        if (animate) {
          // Remove the transition class after it finishes so subsequent
          // pointer drags update instantly.
          clearTimeout(this._animTimer);
          this._animTimer = setTimeout(() => this.img.classList.remove('is-animating'), 240);
        }
      }

      // -- Gestures (PhotoSwipe v5 gestures.js) ------------------------
      _stagePoint(evt) {
        // Cached rect is refreshed on pointerdown/resize, never on every move.
        const r = this._stageRect || (this._stageRect = this.stage.getBoundingClientRect());
        return { x: evt.clientX - r.left, y: evt.clientY - r.top };
      }

      _onPointerDown(evt) {
        // PhotoSwipe rule: never preventDefault on touch (longpress menu),
        // do on mouse to suppress text/image drag.
        if (evt.pointerType === 'mouse') evt.preventDefault();
        // Refresh the stage rect once per gesture (page may have scrolled
        // or resized since the last gesture).
        this._stageRect = this.stage.getBoundingClientRect();
        try { this.stage.setPointerCapture(evt.pointerId); } catch (_) {}
        const p = this._stagePoint(evt);
        this._pointers.set(evt.pointerId, { x: p.x, y: p.y, startX: p.x, startY: p.y });

        if (this._pointers.size === 2) {
          // Begin pinch
          const [a, b] = [...this._pointers.values()];
          this._pinch = {
            d0: dist(a, b) || 1,
            scale0: this._scale,
            cx: (a.x + b.x) / 2,
            cy: (a.y + b.y) / 2,
          };
        } else if (this._pointers.size === 1) {
          this._panning = false;
        }
      }

      _onPointerMove(evt) {
        if (!this._pointers.has(evt.pointerId)) return;
        const p = this._stagePoint(evt);
        const prev = this._pointers.get(evt.pointerId);
        prev.x = p.x; prev.y = p.y;

        if (this._pointers.size === 2 && this._pinch) {
          const [a, b] = [...this._pointers.values()];
          const d = dist(a, b);
          const ratio = d / this._pinch.d0;
          const target = clamp(this._pinch.scale0 * ratio, this._zoom.min, this._zoom.ceil);
          this._scheduleRender(() => {
            this._setZoom(target, { x: this._pinch.cx, y: this._pinch.cy }, false);
          });
          return;
        }

        if (this._pointers.size === 1) {
          const onlyP = prev;
          const moved = Math.hypot(onlyP.x - onlyP.startX, onlyP.y - onlyP.startY);
          // Commit to pan once past hysteresis, only when zoomed in
          if (!this._panning && moved > DRAG_HYSTERESIS && this._scale > this._zoom.fit + 1e-3) {
            this._panning = true;
            this.stage.dataset.panning = 'true';
            this._panStart = { tx: this._tx, ty: this._ty, x: onlyP.startX, y: onlyP.startY };
          }
          if (this._panning) {
            const dx = onlyP.x - this._panStart.x;
            const dy = onlyP.y - this._panStart.y;
            const c = this._correctPan(this._scale, this._panStart.tx + dx, this._panStart.ty + dy);
            this._scheduleRender(() => {
              this._tx = c.tx; this._ty = c.ty;
              this._applyTransform(false);
            });
          }
        }
      }

      _onPointerUp(evt) {
        const tracked = this._pointers.get(evt.pointerId);
        this._pointers.delete(evt.pointerId);

        if (this._pointers.size < 2) this._pinch = null;
        if (this._pointers.size === 0) {
          if (this._panning) {
            this._panning = false;
            this.stage.dataset.panning = 'false';
            // Suppress the synthesized click that follows a drag so it doesn't close the lightbox.
            this._justPanned = true;
            setTimeout(() => { this._justPanned = false; }, 50);
            return;
          }
          // No drag committed — treat as tap (or double-tap)
          if (tracked) {
            const moved = Math.hypot(tracked.x - tracked.startX, tracked.y - tracked.startY);
            if (moved < MIN_TAP_DISTANCE) this._handleTap({ x: tracked.x, y: tracked.y });
          }
        }
      }

      _handleTap(point) {
        const now = Date.now();
        if (now - this._lastTap < DOUBLE_TAP_DELAY
            && this._lastTapPoint
            && dist(this._lastTapPoint, point) < MIN_TAP_DISTANCE) {
          // Double-tap: toggle between fit and secondary at tap point
          this._lastTap = 0;
          this._lastTapPoint = null;
          if (this._scale <= this._zoom.fit + 1e-3) {
            this._setZoom(this._zoom.secondary, point, true);
          } else {
            this._setZoom(this._zoom.fit, this._stageCenter(), true);
          }
        } else {
          this._lastTap = now;
          this._lastTapPoint = point;
        }
      }

      _onWheel(evt) {
        evt.preventDefault();
        if (!this._zoom.imgW) return;
        const point = this._stagePoint(evt);
        const factor = 1 - evt.deltaY * WHEEL_STEP;
        const target = clamp(this._scale * factor, this._zoom.min, this._zoom.ceil);
        this._setZoom(target, point, false);
      }

      _scheduleRender(fn) {
        // rAF coalesce — PhotoSwipe-style
        if (this._rafPending) {
          this._rafQueued = fn;
          return;
        }
        this._rafPending = true;
        requestAnimationFrame(() => {
          this._rafPending = false;
          fn();
          if (this._rafQueued) {
            const q = this._rafQueued;
            this._rafQueued = null;
            q();
          }
        });
      }

      // -- Keyboard ----------------------------------------------------
      _onKey(evt) {
        switch (evt.key) {
          case 'Escape': evt.preventDefault(); this.close(); break;
          case 'ArrowLeft': evt.preventDefault(); this.prev(); break;
          case 'ArrowRight': evt.preventDefault(); this.next(); break;
          case '+': case '=': evt.preventDefault(); this._zoomBy(1.4, this._stageCenter()); break;
          case '-': case '_': evt.preventDefault(); this._zoomBy(1 / 1.4, this._stageCenter()); break;
          case '0': evt.preventDefault(); this._setZoom(this._zoom.fit, this._stageCenter(), true); break;
          default: break;
        }
      }

      _onResize() {
        if (this.getAttribute('data-open') !== 'true') return;
        // Stage dims changed — invalidate cached rect.
        this._stageRect = null;
        this._computeZoomLevels();
        // Re-clamp current position to new viewport.
        const c = this._correctPan(this._scale, this._tx, this._ty);
        this._tx = c.tx; this._ty = c.ty;
        // Bring scale back into the new valid range.
        const newScale = clamp(this._scale, this._zoom.min, this._zoom.ceil);
        this._setZoom(newScale, this._stageCenter(), false);
      }

      _onVariantChange() {
        // PDP variant change can change the visible image set under us.
        // Close to avoid showing a stale slide.
        this.close();
      }

      disconnectedCallback() {
        // Safety net: if the element is removed from the DOM while open
        // (e.g. by a theme-editor section reload), make sure we tear down.
        if (this.getAttribute('data-open') === 'true') this.close();
      }
    }
    customElements.define('image-lightbox', ImageLightbox);
  }

  // ---- Singleton getter ----------------------------------------------
  function getLightbox() {
    let el = document.querySelector('image-lightbox');
    if (!el) {
      el = document.createElement('image-lightbox');
      document.body.appendChild(el);
    }
    return el;
  }

  // ---- Click delegation ----------------------------------------------
  function onGalleryClick(evt) {
    // PhotoSwipe: preserve modifier-click and middle-click navigation
    if (evt.ctrlKey || evt.metaKey || evt.shiftKey || evt.button === 1) return;
    const link = evt.target.closest('a.js-zoom-link');
    if (!link) return;
    const item = link.closest('.media-viewer__item');
    if (!item || item.dataset.mediaType !== 'image') return;
    // Respect explicit hidden state (variant grouping)
    if (item.style.display === 'none') return;
    evt.preventDefault();
    evt.stopPropagation();
    const gallery = evt.currentTarget;
    getLightbox().open(gallery, item);
  }

  function bindGalleries(root) {
    const scope = root || document;
    scope.querySelectorAll('media-gallery[data-lightbox="true"]').forEach((g) => {
      if (g.__lightboxBound) return;
      g.__lightboxBound = true;
      g.addEventListener('click', onGalleryClick);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindGalleries());
  } else {
    bindGalleries();
  }
  // Theme editor: re-bind when a section is reloaded
  document.addEventListener('shopify:section:load', (evt) => bindGalleries(evt.target));
})();
